import { Sheet } from 'react-modal-sheet';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Button, ButtonToggle } from '@peacecover/component';
import { ChevronDownSmall, ChevronUpSmall } from '@peacecover/icon';
import { useState, useEffect, useRef } from 'react';
import BettieAgreementSection from '../../../presentation/chat/components/BettieAgreementSection';
import { useBettieCharge } from '../../../presentation/chat/hooks/useBettieCharge';
import Bubble from '../../../presentation/components/floating/Bubble';
import { BettieGradientColor } from '../../../constants/BettieGradientColor';
import { ReactComponent as WhiteCheckCircle } from '../../../icons/white.check.circle.line.svg';
import type { components } from '../../../types/schema';

interface BettieChargeBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

type ApiV1BettieProduct = components['schemas']['ApiV1BettieProduct'];

const BettieChargeBottomSheet = ({ isOpen, onClose }: BettieChargeBottomSheetProps) => {
  const { colorMode } = useColorMode();
  const {
    products,
    selectedItem,
    originalSelectedItem,
    isItemListOpen,
    setIsItemListOpen,
    handleSelectItem,
    handleSelectBundle,
    handlePrimaryClick,
    isLoadingProducts
  } = useBettieCharge(onClose);
  const [showGradient, setShowGradient] = useState(true);
  const [isOverMaxHeight, setIsOverMaxHeight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 바텀시트 높이가 700px를 넘는지 확인
  useEffect(() => {
    if (!containerRef.current) return;

    const checkHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.scrollHeight;
        setIsOverMaxHeight(height > 700);
      }
    };

    checkHeight();

    const resizeObserver = new ResizeObserver(checkHeight);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isItemListOpen) {
      setShowGradient(true);
      return;
    }

    // 실제 스크롤 가능한 요소 찾기
    const findScrollableElement = (): HTMLElement | null => {
      if (!containerRef.current) return null;

      // Sheet.Container 자체가 스크롤 가능한지 확인
      const container = containerRef.current;
      const containerStyle = getComputedStyle(container);
      if (
        (containerStyle.overflowY === 'auto' || containerStyle.overflowY === 'scroll') &&
        container.scrollHeight > container.clientHeight
      ) {
        return container;
      }

      // 내부에서 스크롤 가능한 요소 찾기
      const allElements = container.querySelectorAll('*');
      for (let i = 0; i < allElements.length; i++) {
        const htmlEl = allElements[i] as HTMLElement;
        const style = getComputedStyle(htmlEl);
        if (
          (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
          htmlEl.scrollHeight > htmlEl.clientHeight
        ) {
          return htmlEl;
        }
      }
      return null;
    };

    const handleScroll = () => {
      setShowGradient(false);
    };

    // 약간의 지연 후 스크롤 가능한 요소 찾기
    const timeoutId = setTimeout(() => {
      const scrollable = findScrollableElement();
      if (scrollable) {
        scrollable.addEventListener('scroll', handleScroll, { passive: true });
      } else {
        // 스크롤 가능한 요소를 찾지 못한 경우, 전체 문서에서 스크롤 이벤트 감지
        const handleDocumentScroll = () => {
          const scrollable = findScrollableElement();
          if (scrollable) {
            document.removeEventListener('scroll', handleDocumentScroll, true);
            scrollable.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll();
          }
        };
        document.addEventListener('scroll', handleDocumentScroll, true);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      const scrollable = findScrollableElement();
      if (scrollable) {
        scrollable.removeEventListener('scroll', handleScroll);
      }
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isItemListOpen]);

  const getButtonText = () => {
    if (!selectedItem) return t('BettieChargeBottomSheet.goToPayment');

    const amount = Number(selectedItem.baseAmount - selectedItem.discountAmount).toLocaleString();
    const currency = selectedItem.currency === 'USD' ? '$' : '원';

    return t('BettieChargeBottomSheet.agreeAndPay', { price: `${amount}${currency}` });
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content">
      <Sheet.Container
        ref={containerRef}
        className="relative !rounded-t-[20px] !bg-background-light dark:!bg-background-elevated !max-h-[700px] overflow-y-auto"
      >
        <div className="px-[24px] pt-[40px] flex flex-col gap-[32px]">
          <div className="flex flex-col gap-[10px] relative">
            {products.some((item) => item.category === 'subscription') && (
              <Bubble
                text={t('BettieChargeBottomSheet.recommend')}
                gradientDirection="center"
                className="!absolute left-[36px] top-[-20px] z-[1] pointer-events-none"
              />
            )}
            {isLoadingProducts ? (
              <BettieProductSelectSkeleton />
            ) : (
              <>
                {(isItemListOpen ? products : products.slice(0, 1)).map((item) => {
                  const isSubscription = item.category === 'subscription';
                  return (
                    <div
                      key={item.bettieProductId}
                      className={
                        isSubscription
                          ? 'p-[1px] rounded-[17px] shadow-[0_0_2px_0_rgba(0,0,0,0.05),_0_0_8px_0_rgba(0,0,0,0.07)] bg-gradient-multi-rainbox-lv2'
                          : 'rounded-[16px] dark:border dark:border-[#141414] shadow-[0_0_2px_0_rgba(0,0,0,0.05),_0_0_8px_0_rgba(0,0,0,0.07)] dark:shadow-[0_0_3px_0_rgba(0,0,0,0.95),_0_0_12px_0_rgba(0,0,0,0.00)]'
                      }
                    >
                      <button
                        type="button"
                        className={
                          isSubscription
                            ? 'flex items-center justify-between px-[19px] py-[19px] dark:px-[20px] dark:py-[20px] rounded-[16px] w-full'
                            : 'flex items-center justify-between px-[20px] py-[20px] w-full'
                        }
                        style={
                          isSubscription
                            ? {
                                background:
                                  BettieGradientColor.bettieProductFill[colorMode].background
                              }
                            : undefined
                        }
                        onClick={() => handleSelectItem(item.bettieProductId)}
                      >
                        <ChargeItem
                          product={item}
                          isSelected={
                            originalSelectedItem?.bettieProductId === item.bettieProductId
                          }
                          selectedItem={selectedItem}
                          onSelectBundle={handleSelectBundle}
                        />
                      </button>
                    </div>
                  );
                })}
                {!isLoadingProducts && products.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setIsItemListOpen((prev) => !prev)}
                    className="text-[13px] leading-[16px] text-text-main-lv3 flex items-center justify-center gap-[4px]"
                  >
                    {isItemListOpen
                      ? t('BettieChargeBottomSheet.collapse')
                      : t('BettieChargeBottomSheet.loadMore')}
                    {isItemListOpen ? (
                      <ChevronUpSmall size={18} className="text-text-main-lv3" />
                    ) : (
                      <ChevronDownSmall size={18} className="text-text-main-lv3" />
                    )}
                  </button>
                )}
              </>
            )}
          </div>
          <BettieAgreementSection />
          <div className="sticky bottom-0">
            {isItemListOpen && showGradient && isOverMaxHeight && (
              <div className="h-[84px] bg-[linear-gradient(180deg,rgba(255,255,255,0.00)_0%,#FFF_100%)] dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.00)_0%,#000_100%)]" />
            )}
            <div className="bg-background-light dark:bg-background-elevated">
              <Button
                size="large"
                className="mx-[0px] rounded-[18px] text-[17px] font-semibold leading-[20px] h-[56px] w-full"
                onClick={handlePrimaryClick}
              >
                {getButtonText()}
              </Button>
            </div>
          </div>
        </div>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-[rgba(0,0,0,0.50)]" />
    </Sheet>
  );
};

export default BettieChargeBottomSheet;

const ChargeItem = ({
  product,
  isSelected,
  selectedItem,
  onSelectBundle
}: {
  product: ApiV1BettieProduct;
  isSelected: boolean;
  selectedItem: ApiV1BettieProduct | null;
  onSelectBundle: (bundle: ApiV1BettieProduct | null) => void;
}) => {
  const amount = Number(product.baseAmount - product.discountAmount).toLocaleString();

  const [title, description] = product.name.split('/');

  const displayAmount = (amount: string) => {
    switch (product.currency) {
      case 'KRW':
        return `${amount}원`;
      case 'USD':
        return `${amount}$`;
      default:
        return `${amount}원`;
    }
  };

  return (
    <div className="flex flex-col gap-[20px] w-full">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <ButtonToggle type="check" size="lg" checked={isSelected} />
          <p className="flex flex-col gap-[2px] items-start text-[15px] font-semibold leading-[18px] text-text-main-lv2 text-left max-w-[187px] ml-[6px] mr-[4px]">
            {title.trim()}
            <span className="text-[12px] font-normal text-text-main-lv3">
              {description?.trim() && `${description.trim()} · `}
              {product.category === 'subscription'
                ? t('BettieChargeBottomSheet.monthly')
                : t('BettieChargeBottomSheet.oneTime')}
            </span>
          </p>
        </div>
        <p className="text-[15px] font-bold leading-[18px] text-text-primary">
          {displayAmount(amount)}
          {product.category === 'subscription' && (
            <span className="text-[12px] font-normal ml-[2px]">/월</span>
          )}
        </p>
      </div>
      {isSelected && product?.bundles?.length > 0 && (
        <div className="flex flex-col pt-[16px] gap-[14px] border-t border-secondary-light dark:border-secondary-dark">
          <p className="bg-gradient-single-red bg-clip-text text-transparent text-[13px] font-semibold leading-[16px]">
            {t('BettieChargeBottomSheet.purchaseWithBundle')}
          </p>
          {product.bundles?.map((bundle) => {
            const isBundleSelected = selectedItem?.bettieProductId === bundle.bettieProductId;
            return (
              <button
                key={bundle.bettieProductId}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectBundle(isBundleSelected ? null : (bundle as ApiV1BettieProduct));
                }}
                className="p-[16px] bg-secondary-light dark:bg-secondary-dark rounded-[12px] flex items-center justify-between border border-separator1"
              >
                <div className="flex items-center gap-[7px]">
                  {isBundleSelected ? (
                    <ButtonToggle type="check" size="lg" checked={isBundleSelected} />
                  ) : (
                    <WhiteCheckCircle className="w-[24px] h-[24px]" />
                  )}
                  <p className="flex flex-col gap-[2px] items-start text-[15px] font-semibold leading-[18px] text-text-main-lv2">
                    {bundle.description}
                    <span className="text-[12px] font-normal">
                      {t('BettieChargeBottomSheet.oneTime')}
                    </span>
                  </p>
                </div>
                <div className="flex flex-col gap-[2px] items-end">
                  <p className="text-[15px] font-semibold leading-[18px] text-text-primary">
                    {displayAmount(
                      (
                        bundle.baseAmount -
                        product.baseAmount -
                        bundle.discountAmount
                      ).toLocaleString()
                    )}
                  </p>
                  <p className="text-[12px] font-medium text-text-sub-lv1 line-through">
                    {displayAmount((bundle.baseAmount - product.baseAmount).toLocaleString())}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const BettieProductSelectSkeleton = () => {
  return (
    <div className="flex items-center justify-between rounded-[16px] dark:border dark:border-[#141414] shadow-[0_0_2px_0_rgba(0,0,0,0.05),_0_0_8px_0_rgba(0,0,0,0.07)] dark:shadow-[0_0_3px_0_rgba(0,0,0,0.95),_0_0_12px_0_rgba(0,0,0,0.00)] px-[20px] py-[20px] animate-pulse">
      <div className="flex items-center gap-[6px]">
        <div className="h-[24px] w-[24px] rounded-full bg-gray-100 dark:bg-gray-800" />
        <div className="h-[18px] w-[160px] rounded-full bg-gray-100 dark:bg-gray-800" />
      </div>
      <div className="h-[18px] w-[60px] rounded-full bg-gray-100 dark:bg-gray-800" />
    </div>
  );
};
